import React, {useState, useCallback, useMemo} from 'react';
import {Flex, Icon, PriorityIcon, Title} from '@priority-software/priority-style-react';
import {priority} from '@priority-software/priority-proto-api/lib';
import cx from 'classnames';
import {showFormClientSideMessage} from '../../../../../actions/messages';
import {createDataTestId} from '../../../../../../../utils/dataTestId';
import {texts} from '../../texts';
import {AdvancedSearchTitle} from './parts/AdvancedSearchTitle';
import {SavedSearches} from '../SavedSearches';
import {SavedSearch} from '../../../../../types';
import {TriggerButton} from '../SavedSearches/parts/Trigger/TriggerButton';
import {ModernTooltip} from '../../../../../../../components/Common/Tooltip/ModernTooltip';
import styles from '../../styles.module.scss';

type Props = {
    session: string;
    isSavedSearch: boolean;
    formName: string;
    searchTitle: string;
    filterCount?: number;
    searches: SavedSearch[];
    recentSearches: SavedSearch[];
    onDrawerClose: () => void;
    updateTitle: (title: string) => void;
    handleExecuteSearch: (search: SavedSearch) => void;
    handleDeleteSearch: (id: string) => void;
    handleToggleDefault: (id: string) => void;
    onReorder: (data: SavedSearch[]) => void;
};

export const AdvancedSearchHeader = (props: Props) => {
    const {
        session,
        isSavedSearch,
        formName,
        searchTitle,
        filterCount,
        searches,
        recentSearches,
        updateTitle,
        handleExecuteSearch,
        handleDeleteSearch,
        handleToggleDefault,
        onReorder,
        onDrawerClose
    } = props;
    const [isEditTitle, toggleEditTitle] = useState(false);
    const [isOpenSavedSearches, toggleSavedSearches] = useState(false);

    const handleOpenSavedSearch = useCallback(() => toggleSavedSearches(true), []);
    const handleCloseSavedSearch = useCallback(() => toggleSavedSearches(false), []);

    const showSearchHelp = () => {
        showFormClientSideMessage(session, priority.netitems.shared.Message.Type.Information, {
            content: texts.searchHelpContent,
            title: texts.searchHelpTitle,
            confirmButtonText: texts.okLabel
        });
    };

    const savedSearchTriggerTitle = useMemo(() => {
        if (isSavedSearch || recentSearches.find(s => s.name === searchTitle)) {
            return searchTitle;
        }
        return texts.searchesTitle;
    }, [isSavedSearch, recentSearches, searchTitle]);

    return (
        <div className={styles.headerContainer}>
            <Flex alignItems="flex-start" componentClasses={styles.topHeader}>
                <Title
                    skin="h2"
                    componentClasses={styles.title}
                    {...createDataTestId('advanced-search-title', true)}
                >
                    {texts.advancedSearch}
                    <span
                        className={styles.titleFormName}
                        {...createDataTestId('advanced-search-form-name', false)}
                    >
                        {formName}
                    </span>
                </Title>

                <Flex
                    justifyContent="flex-end"
                    componentClasses={styles.headerToolbar}
                    alignItems="center"
                >
                    <Icon
                        onClick={showSearchHelp}
                        iconColor="secondary-500"
                        icon={PriorityIcon.questionCircle}
                        size={'small'}
                        componentClasses={cx(styles.headerIcon, styles.questionIcon)}
                    />
                    <div className={styles.separatorBorder} />
                    <ModernTooltip tooltip={texts.closeTooltip} delay={300} position="top">
                        <Icon
                            onClick={onDrawerClose}
                            iconColor="secondary-500"
                            size={'small'}
                            icon={PriorityIcon.closeBig}
                            componentClasses={styles.headerIcon}
                        />
                    </ModernTooltip>
                </Flex>
            </Flex>
            <div
                className={styles.searchesEditContainer}
                {...createDataTestId('advanced-search-edit-title-container', false)}
            >
                {isEditTitle ? (
                    <AdvancedSearchTitle
                        title={searchTitle}
                        onSave={updateTitle}
                        onClose={() => toggleEditTitle(false)}
                    />
                ) : (
                    <SavedSearches
                        recentSearches={recentSearches}
                        isOpen={isOpenSavedSearches}
                        onClose={handleCloseSavedSearch}
                        onOpen={handleOpenSavedSearch}
                        onReorder={onReorder}
                        searches={searches}
                        executeSearch={handleExecuteSearch}
                        deleteSearch={handleDeleteSearch}
                        toggleDefault={handleToggleDefault}
                        filterCount={filterCount}
                        hideUnsavedSearches
                        trigger={
                            <TriggerButton
                                filtersCount={filterCount}
                                text={savedSearchTriggerTitle}
                                paddingX="1px"
                            />
                        }
                    />
                )}

                {!isOpenSavedSearches && !isEditTitle && isSavedSearch ? (
                    <ModernTooltip tooltip={texts.editSearchTooltip} delay={300} position="top">
                        <Icon
                            onClick={() => toggleEditTitle(true)}
                            icon={PriorityIcon.editFill}
                            componentClasses={styles.editIcon}
                        />
                    </ModernTooltip>
                ) : null}
            </div>
        </div>
    );
};
